import { generateClient } from 'aws-amplify/api';
import { format, fromUnixTime, isWithinInterval, isAfter, startOfMonth, endOfMonth, endOfDay, startOfDay, formatISO } from 'date-fns';
import Cookies from 'js-cookie';
import store from '@/store';
import router from '@/router';

import * as subscriptions from '../amplifyGraphql/subscriptions';
import * as kalaSubscriptions from '../amplifyGraphql/kala/subscriptions';

import { computeSession } from './index';

const unsubscribe = async () => {
  const { amplifySubscription } = store.state;

  if (amplifySubscription && Object.keys(amplifySubscription).length) {
    Object.values(amplifySubscription).map(subscription => {
      subscription.unsubscribe();
    });

    store.commit('setAmplifySubscription', null);
  }
};

const isSessionFromProduct = session => {
  const { siteInfo, product, productServices } = store.state;

  if (siteInfo.is_internal_session) {
    return (session.product_activity || []).some(pa => pa.product === product.uuid);
  }

  return productServices.some(p => session.products.includes(p.serviceId.toString()));
};

const isSessionAvailable = session => {
  const { siteInfo, cartItem } = store.state;
  const { selectedMonth, selectedDate } = cartItem;

  const today = new Date();

  const sessionHaveProperty = siteInfo.is_internal_session ? session.workspace : session.codeSiteComptage;
  if (!session || !sessionHaveProperty) {
    return false;
  }

  if (selectedDate && router.currentRoute.name === 'Session') {
    const date = new Date(selectedDate);
    const start = isAfter(new Date(date), today) ? startOfDay(new Date(date)) : today;
    const isBetween = isWithinInterval(fromUnixTime(session.start_time), { start: start, end: endOfDay(start) });

    if (!isBetween) {
      return false;
    }
  }

  if (selectedMonth && router.currentRoute.name === 'Calendar') {
    const date = new Date(selectedMonth);
    const start = isAfter(new Date(date), today) ? startOfMonth(new Date(date)) : today;

    const isBetween = isWithinInterval(fromUnixTime(session.start_time), { start: start, end: endOfMonth(start) });

    if (!isBetween) {
      return false;
    }
  }

  const isSessionFromWorkspace = siteInfo.is_internal_session ? session.workspace === siteInfo.workspace.toUpperCase() : siteInfo.code_site_comptage.toString() === session.codeSiteComptage.toString();

  if (!isSessionFromWorkspace) {
    return false;
  }

  const isProductAvailable = isSessionFromProduct(session);

  if (!isProductAvailable) {
    return false;
  }

  const capacity = session.capacity !== null && session.capacity !== undefined ? session.capacity : session.max_participant;

  if (!capacity) {
    return false;
  }

  const participantsAmount = session.participantsAmount !== null && session.participantsAmount !== undefined ? session.participantsAmount : (session.participants || []).length;
  return capacity > participantsAmount + (session?.pre_reserved_participants?.items || []).length;
};

const handleAddParticipantToHoranetSession = (session, workspace, startDateISO, endDateISO) => {
  const filterStartDate = startDateISO;
  const filterEndDate = endDateISO;
  const startDataInInterval = isWithinInterval(new Date(session.startDate), { start: new Date(filterStartDate), end: new Date(filterEndDate) });
  const endDataInInterval = isWithinInterval(new Date(session.endDate), { start: new Date(filterStartDate), end: new Date(filterEndDate) });

  const isBetween = startDataInInterval && endDataInInterval;

  if (workspace !== session.codeSiteComptage || !isBetween) {
    return;
  }
  const storeSessions = [...store.state.sessions?.items || []];

  const { availableDates } = store.state;

  let haveChange = false;

  const isAvailable = isSessionAvailable(session);
  const storeSessionIndex = storeSessions.findIndex(s => s.code.toString() === session.id.toString());

  if (!isAvailable) {
    if (storeSessionIndex !== -1) {
      storeSessions.splice(storeSessionIndex, 1);
      haveChange = true;
    }

    const key = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');

    if (availableDates[key]?.length) {
      const dateIndex = availableDates[key].findIndex(s => s === session.id);

      if (dateIndex !== -1) {
        availableDates[key].splice(dateIndex, 1);
        haveChange = true;
      }
    }
  }

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

const handleDeleteParticipantFromHoranetSession = session => {
  const storeSessions = [...store.state.sessions?.items || []];

  const { availableDates, siteInfo } = store.state;

  const { is_internal_session } = siteInfo;

  let haveChange = false;

  const storeSessionIndex = storeSessions.findIndex(s => s.code.toString() === session.id.toString());

  const isAvailable = isSessionAvailable(session);

  if (isAvailable) {
    if (storeSessionIndex === -1) {
      storeSessions.push(computeSession(session, is_internal_session));
      haveChange = true;
    }

    const key = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');
    if (!availableDates[key]) {
      availableDates[key] = [];
    }

    if (!availableDates[key].includes(session.id)) {
      availableDates[key].push(session.id);
      haveChange = true;
    }
  }

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

// todo: test
const handleSessionCreateEdit = session => {
  const storeSessions = [...store.state.sessions?.items || []];
  const { siteInfo, availableDates } = store.state;

  const { is_internal_session } = siteInfo;

  let haveChange = false;

  const isAvailable = isSessionAvailable(session);
  const key = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');
  const storeSessionIndex = storeSessions.findIndex(s => s.code.toString() === session.id.toString());

  if (isAvailable) {
    if (storeSessionIndex === -1) {
      storeSessions.push(computeSession(session, is_internal_session));
      haveChange = true;
    }

    if (storeSessionIndex !== -1) {
      storeSessions[storeSessionIndex] = computeSession(session, is_internal_session);
      haveChange = true;
    }

    if (!availableDates[key]) {
      availableDates[key] = [];
    }

    if (!availableDates[key].includes(session.id)) {
      availableDates[key].push(session.id);
      haveChange = true;
    }
  }

  if (!isAvailable) {
    if (storeSessionIndex !== -1) {
      storeSessions.splice(storeSessionIndex, 1);
      haveChange = true;
    }

    if (availableDates[key]?.length) {
      const dateIndex = availableDates[key].findIndex(s => s === session.id);
      if (dateIndex !== -1) {
        availableDates[key].splice(dateIndex, 1);
        haveChange = true;
      }
    }
  }

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

const handleDeleteSession = session => {
  const storeSessions = [...store.state.sessions?.items || []];

  const { availableDates } = store.state;

  const key = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');

  let haveChange = false;

  if (availableDates[key]?.length) {
    const dateIndex = availableDates[key].findIndex(s => s === session.id);
    if (dateIndex !== -1) {
      availableDates[key].splice(dateIndex, 1);
      haveChange = true;
    }
  }

  const sessionIndex = storeSessions.findIndex(s => s.code.toString() === session.id.toString());

  if (sessionIndex !== -1) {
    storeSessions.splice(sessionIndex, 1);
    haveChange = true;
  }

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

const handleUpdateSession = s => {
  const session = { ...s, participants: s.participants ? JSON.parse(s.participants) : [] };
  const storeSessions = [...store.state.sessions?.items || []];
  const { availableDates, siteInfo } = store.state;

  const { is_internal_session } = siteInfo;

  let haveChange = false;
  const availableDateKey = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');
  const auxiliarSession = computeSession(s, is_internal_session);
  const isAvailable = isSessionAvailable(session);

  const sessionIndex = storeSessions.findIndex(storeSession => storeSession.code.toString() === session.id.toString());

  if (isAvailable) {
    if (sessionIndex !== -1) {
      storeSessions[sessionIndex] = auxiliarSession;
      haveChange = true;
    } else {
      storeSessions.push(auxiliarSession);
      haveChange = true;
    }
    if (!availableDates[availableDateKey]) {
      availableDates[availableDateKey] = [];
    }
    haveChange = true;
    availableDates[availableDateKey].push(session.id);
  }

  if (!isAvailable) {
    if (sessionIndex !== -1) {
      storeSessions.splice(sessionIndex, 1);
      haveChange = true;
    }
    const availableIndex = availableDates[availableDateKey]?.findIndex(sessionId => sessionId === session.id);

    if (availableIndex !== -1) {
      availableDates[availableDateKey].splice(availableIndex, 1);
      haveChange = true;
    }
  }

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

const handleReplicaSession = item => {
  const storeSessions = [...store.state.sessions?.items || []];
  const { availableDates, siteInfo } = store.state;
  const { is_internal_session } = siteInfo;
  const indexToDelete = {};

  let haveChange = false;

  (item.delete || []).map(session => {
    const sessionIndex = storeSessions.findIndex(s => s.code.toString() === session.id.toString());

    if (sessionIndex !== -1) {
      storeSessions.splice(sessionIndex, 1);
      haveChange = true;
    }

    Object.keys(availableDates).map(key => {
      const index = availableDates[key].findIndex(s => s === session.id);
      if (index !== -1) {
        if (!indexToDelete[key]) {
          indexToDelete[key] = [];
        }
        indexToDelete[key].push(index);

        indexToDelete[key] = indexToDelete[key].sort((a, b) => (a < b ? -1 : 1));

        haveChange = true;
      }
    });
  });

  (item.create || []).map(session => {
    const isAvailable = isSessionAvailable(session);

    if (isAvailable) {
      const auxiliarSession = computeSession(session, is_internal_session);
      storeSessions.push(auxiliarSession);
      haveChange = true;
    }

    const availableDateKey = format(fromUnixTime(session.start_time), 'yyyy-MM-dd');

    if (!availableDates[availableDateKey]) {
      availableDates[availableDateKey] = [];
    }
    haveChange = true;
    availableDates[availableDateKey].push(session.id);
  });

  (item.edit || []).map(session => {
    handleUpdateSession(session);
  });

  Object.keys(indexToDelete).map(key => {
    for (let i = indexToDelete[key].length - 1; i >= 0; i -= 1) {
      availableDates[key].splice(indexToDelete[key][i], 1);
    }
  });

  if (haveChange) {
    store.commit('setSessions', storeSessions);
    store.commit('setAvailableDates', availableDates);
  }
};

const buildKalaFilter = (startDate, endDate) => {
  const { siteInfo } = store.state;

  const filter = {
    and: [
      { startDate: { gt: formatISO(startDate) } },
      { endDate: { lt: formatISO(endDate) } },
      { workspace: { eq: siteInfo.code_site_comptage.toString() } },
      { allowReservation: { eq: true } }
    ]
  };

  return filter;
};

const buildKalaCreateFilter = (startDate, endDate) => {
  const filter = buildKalaFilter(startDate, endDate);

  filter.and.push({ horanetStatus: { ne: '1' } });

  return filter;
};

const buildPegaseFilter = (startDate, endDate) => {
  const { siteInfo } = store.state;
  const start = parseFloat(format(startDate, 't'));
  const end = parseFloat(format(endDate, 't'));

  const filter = { start_time: { between: [start, end] } };

  if (siteInfo.is_internal_session) {
    filter.workspace = { eq: siteInfo.workspace.toUpperCase() };
  }

  if (!siteInfo.is_internal_session && ['postgres'].includes(siteInfo.horanetSource)) {
    filter.codeSiteComptage = { eq: parseInt(siteInfo.code_site_comptage) };
  }

  return filter;
};

const handlePegaseSubscriptions = (client, cookie, filter) => {
  const amplifySubscriptions = {};
  const { siteInfo } = store.state;

  // horanet endpoints
  amplifySubscriptions.addParticipant = client.graphql({ query: subscriptions.onAddParticipantToSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      handleAddParticipantToHoranetSession(data.onAddParticipantToSession);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteParticipant = client.graphql({ query: subscriptions.onDeleteParticipantFromSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      handleDeleteParticipantFromHoranetSession(data.onDeleteParticipantFromSession);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.handleSessionCreateEdit = client.graphql({ query: subscriptions.onHandleCreateEditSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      handleSessionCreateEdit(data.onHandleCreateEditSession);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteSession = client.graphql({ query: subscriptions.onDeleteSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      handleDeleteSession(data.onDeleteSession);
    },
    error: error => {
      console.warn(error);
    }
  });

  // internal
  amplifySubscriptions.updateSession = client.graphql({ query: subscriptions.onUpdateSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      handleUpdateSession(data.onUpdateSession);
    },
    error: error => {
      console.warn(error);
    }
  });

  if (siteInfo.is_internal_session) {
    amplifySubscriptions.handleReplicaSessions = client.graphql({ query: subscriptions.onHandleUpdateReplicaSessions, variables: { filter: { workspace: { eq: siteInfo.workspace.toUpperCase() } } }, authToken: cookie }).subscribe({
      next: ({ data }) => {
        if (data?.onHandleUpdateReplicaSessions) {
          handleReplicaSession(data.onHandleUpdateReplicaSessions);
        }
      },
      error: error => {
        console.warn(error);
      }
    });
  }

  return amplifySubscriptions;
};

const transformKalaSessionIntoPegaseSession = session => ({
  ...session,
  start_time: parseInt(format(new Date(session.startDate), 't')),
  end_time: parseInt(format(new Date(session.endDate), 't')),
  products: (session?.serviceIds || []).map(s => s.id),
  participantsAmount: (session?.contracts || []).reduce((acc, { metadata }) => acc + (metadata?.reservationAmount || 1), 0)
});

const isSessionToHide = session => {
  if (session.horanetStatus === '1') {
    return true;
  }

  if (!session.visibleOnline) {
    return true;
  }

  return false;
};

const handleKalaSubscriptions = (client, token, filter, createFilter, workspace, startDateISO, endDateISO) => {
  const cookie = `Bearer ${token}`;
  const amplifySubscriptions = {};

  amplifySubscriptions.createSession = client.graphql({ query: kalaSubscriptions.onCreateSession, variables: { filter: createFilter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      if (data.onCreateSession?.visibleOnline !== true) {
        return;
      }

      const session = transformKalaSessionIntoPegaseSession(data.onCreateSession);
      handleSessionCreateEdit(session);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.updateSession = client.graphql({ query: kalaSubscriptions.onUpdateSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      const session = transformKalaSessionIntoPegaseSession(data.onUpdateSession);
      if (isSessionToHide(session)) {
        handleDeleteSession(session);
        return;
      }
      handleSessionCreateEdit(session);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteSession = client.graphql({ query: kalaSubscriptions.onDeleteSession, variables: { filter: filter }, authToken: cookie }).subscribe({
    next: ({ data }) => {
      // compute session body to match schema
      const session = transformKalaSessionIntoPegaseSession(data.onDeleteSession);
      handleDeleteSession(session);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.addParticipantToSession = client.graphql({ query: kalaSubscriptions.onAddContractToSession, variables: {}, authToken: cookie }).subscribe({
    next: ({ data }) => {
      // compute session body to match schema
      const session = transformKalaSessionIntoPegaseSession(data.onAddContractToSession.session);
      handleAddParticipantToHoranetSession(session, workspace, startDateISO, endDateISO);
    },
    error: error => {
      console.warn(error);
    }
  });

  amplifySubscriptions.deleteParticipantFromSession = client.graphql({ query: kalaSubscriptions.onRemoveContractsFromSession, variables: {}, authToken: cookie }).subscribe({
    next: ({ data }) => {
      // compute session body to match schema
      // const session = transformKalaSessionIntoPegaseSession(data.onRemoveContractsFromSession.session);
      // handleDeleteParticipantFromHoranetSession(session);
    },
    error: error => {
      console.warn(error);
    }
  });
};

const handleSubscription = async () => {
  const isUsingKala = process.env.VUE_APP_USING_KALA_AMPLIFY === 'true';

  const { siteInfo, cartItem } = store.state;

  if (!siteInfo.is_internal_session) {
    return;
  }

  const cookie = Cookies.get('id_token');

  const [year, month] = (cartItem.selectedMonth || '').split('-').map(Number);
  if (!year || !month) {
    console.log('handleSubscription: selectedMonth is missing or invalid, skipping', cartItem.selectedMonth);
    return;
  }
  // Use explicit constructor to avoid Date string-parsing inconsistencies with "YYYY-M" format
  const startDate = startOfMonth(new Date(year, month - 1));
  const endDate = endOfMonth(new Date(year, month - 1));

  const filter = isUsingKala && !siteInfo.is_internal_session ? buildKalaFilter(startDate, endDate) : buildPegaseFilter(startDate, endDate);
  const createFilter = isUsingKala && !siteInfo.is_internal_session ? buildKalaCreateFilter(startDate, endDate) : filter;

  // const reservationFilter = { 'session.workspace': { eq: siteInfo.code_site_comptage.toString() } };

  // if (startDate && endDate) {
  //   reservationFilter['session.workspace.startDate'] = { between: [formatISO(startDate), formatISO(endDate)] };
  // }
  const workspace = siteInfo.code_site_comptage?.toString();
  const startDateISO = formatISO(startDate);
  const endDateISO = formatISO(endDate);

  await unsubscribe();
  const client = generateClient({
    endpoint: isUsingKala && !siteInfo.is_internal_session ? process.env.VUE_APP_KALA_APPSYNC_GRAPHQLENDPOINT : process.env.VUE_APP_AWS_APPSYNC_GRAPHQLENDPOINT,
    authMode: 'lambda'
  });
  const amplifySubscriptions = isUsingKala && !siteInfo.is_internal_session ? handleKalaSubscriptions(client, cookie, filter, createFilter, workspace, startDateISO, endDateISO) : handlePegaseSubscriptions(client, cookie, filter);

  // set store
  store.commit('setAmplifySubscription', amplifySubscriptions);
};

export default handleSubscription;
